import is from 'is_js';
import { ApiResponseHelper } from 'omni-common-ui';
import { accessors as idpAccessors } from 'data/IndividualDevelopmentPlans';

const SPEAKING_1 = 'Speaking 1';
const SPEAKING_2 = 'Speaking 2';
const SPEAKING = 'Speaking';

const ASSESSMENT_SKILL_DISPLAY_ORDER = [
  'Vocabulary',
  'Grammar',
  'Phonics',
  'Reading',
  'Listening',
  'Writing',
  SPEAKING,
];

export function getAssessmentPlanFromState(state, groupId) {
  const progressTestsPlan = state.get('rootReducer').get('progressTestsPlan').get(groupId);
  if (!progressTestsPlan || !progressTestsPlan.data) return null;

  const assessmentExams = progressTestsPlan.data
    .filter((ptp) => ptp.isUnitQuiz === false)
    .toList()
    .sortBy((ptp) => ptp.index);

  return assessmentExams.last() || null;
}

export function getAssessmentProgressTestFromState(state, assessmentPlan) {
  if (!assessmentPlan) return null;
  return state.get('rootReducer').get('progressTests').get(assessmentPlan.id);
}

export function calculateAssessmentAverage(skillScorePercentages) {
  if (!skillScorePercentages || skillScorePercentages.size <= 0) return null;

  let total = 0;
  let count = 0;
  skillScorePercentages.forEach((percentage) => {
    if (is.number(percentage)) {
      total += Math.min(100, percentage);
      count += 1;
    }
  });

  if (count === 0) return null;
  return Math.round(total / count);
}

export function buildAssessmentScoreMap(idpList, assessmentData) {
  if (!idpList || !idpList.size || !assessmentData) return null;

  const { studentProgressTestResults, skills } = assessmentData;
  if (!skills || !skills.size) return null;

  const result = {};

  idpList.forEach((idp) => {
    const studentId = `${idp.studentId}`;
    const studentResult = studentProgressTestResults.get(studentId);
    if (!studentResult || !studentResult.totalScore) return;

    const { skillScorePercentages } = studentResult.totalScore;
    if (!skillScorePercentages) return;

    const studentScores = {};
    skills.forEach((skill) => {
      const assessmentPercentage = skillScorePercentages.get(`${skill.skillId}`);
      if (is.number(assessmentPercentage)) {
        studentScores[skill.name] = `${assessmentPercentage}%`;
      }
    });

    if (Object.keys(studentScores).length > 0) {
      result[studentId] = studentScores;
    }
  });

  return Object.keys(result).length > 0 ? result : null;
}

export function normalizeAssessmentSkillLabels(scores) {
  if (!scores) return {};

  const normalized = { ...scores };
  const speakingScore = normalized[SPEAKING_1] || normalized[SPEAKING_2];

  delete normalized[SPEAKING_1];
  delete normalized[SPEAKING_2];

  if (speakingScore) {
    normalized[SPEAKING] = speakingScore;
  }

  return normalized;
}

export function getAssessmentScoresForStudent(studentId, idpList, assessmentData) {
  const scoreMap = buildAssessmentScoreMap(idpList, assessmentData);
  if (!scoreMap) return null;

  const studentScores = scoreMap[`${studentId}`];
  if (!studentScores) return null;

  return normalizeAssessmentSkillLabels(studentScores);
}

function sortSkillResults(skillResults) {
  const getSortIndex = (name) => {
    const index = ASSESSMENT_SKILL_DISPLAY_ORDER.indexOf(name);
    return index === -1 ? ASSESSMENT_SKILL_DISPLAY_ORDER.length : index;
  };

  return [...skillResults].sort((a, b) => getSortIndex(a.name) - getSortIndex(b.name));
}

export function getAssessmentResultsCardInfo(state, { groupId, groupIdpId, studentId }) {
  const groupIdpList = state.get('rootReducer').get('individualDevelopmentPlanList');
  const idpList = idpAccessors.getIdpList(groupIdpList, groupIdpId);
  if (!idpList || !idpList.size) return null;

  const assessmentPlan = getAssessmentPlanFromState(state, groupId);
  const assessmentProgressTest = getAssessmentProgressTestFromState(state, assessmentPlan);

  if (!ApiResponseHelper.hasSucceeded(assessmentProgressTest)) return null;

  const studentScores = getAssessmentScoresForStudent(
    studentId,
    idpList,
    assessmentProgressTest.data,
  );
  if (!studentScores || Object.keys(studentScores).length === 0) return null;

  const idp = idpList.find((item) => `${item.studentId}` === `${studentId}`);
  if (!idp) return null;

  const studentResult = assessmentProgressTest.data.studentProgressTestResults.get(`${studentId}`);
  const skillScorePercentages = studentResult
    && studentResult.totalScore
    && studentResult.totalScore.skillScorePercentages;
  const average = calculateAssessmentAverage(skillScorePercentages);

  const skillResults = sortSkillResults(
    Object.keys(studentScores).map((name) => ({
      name,
      score: studentScores[name],
    })),
  );

  return {
    totalScore: is.number(average) ? `${average}%` : '–',
    skillResults,
  };
}

export function getStudentAssessmentScores(idpList, assessmentData) {
  let scores = new Map();
  if (!idpList || idpList.size <= 0 || !assessmentData) return scores;

  const { studentProgressTestResults } = assessmentData;

  idpList.forEach((idp) => {
    const studentId = `${idp.studentId}`;
    const studentResult = studentProgressTestResults.get(studentId);
    if (!studentResult || !studentResult.totalScore) return;

    const { skillScorePercentages } = studentResult.totalScore;
    const avg = calculateAssessmentAverage(skillScorePercentages);
    if (is.number(avg)) {
      scores = scores.set(studentId, avg);
    }
  });

  return scores;
}



// WEBPACK FOOTER //
// ./containers/Group/ProgressReportHFv4/assessmentScores.js